import { ZagentMiniConfig, ZagentAgentType, ZagentMiniAgentEntry, AGENT_PROVIDER_MAP } from '@zerops/zerops/core/zerops-services';
import {
  RecipeHandoffPayload,
  RecipeHandoffZagentConfig,
  ZagentAgentConfig
} from './recipe-handoff-base.action';

/**
 * Adapters between the UI's `ZagentMiniConfig` and the wire
 * `RecipeHandoffPayload`. The wire shape has two mutually-exclusive variants
 * (see the payload interface for the full contract):
 *
 *   - Legacy `zagent` — single-agent. Still READ for safety; no longer
 *     emitted by this producer.
 *   - `agentConfigs` (peer-agent map) + root-level container toggles — the
 *     shape this producer emits. `agentConfigs` is OMITTED entirely when no
 *     agent is enabled (IDE-only / bare import). `enableAgent` is derived
 *     downstream: true IFF `agentConfigs` has >=1 entry.
 *
 * Consumer is always dual-shape aware so the app keeps working regardless of
 * which producer (frontend, marketing site) wrote the payload.
 */

/**
 * Build the agent-related slice of a recipe-handoff payload from a
 * `ZagentMiniConfig`. Emits the `agentConfigs` peer-agent map (one entry per
 * enabled agent, in `c.agents` order) plus the root-level container toggles.
 *
 * `agentConfigs` is OMITTED entirely when no agent is enabled — the backend
 * rejects an empty map, and an absent map is the wire signal for "IDE-only /
 * no agents". Never emits both `zagent` and `agentConfigs`.
 */
export function toHandoffPayloadSlice(c: ZagentMiniConfig): Partial<RecipeHandoffPayload> {
  const root: Partial<RecipeHandoffPayload> = {
    enableVscode: c.enableVscode,
    enablePublicAccess: c.enablePublicAccess,
    vscodePassword: c.vscodePassword,
    selectedServices: c.selectedServices
  };

  // Prefer the full enabled-agent list; fall back to the single legacy mirror
  // entry only when an agent is actually enabled and `agents` is absent.
  const entries: ZagentMiniAgentEntry[] = (c.agents && c.agents.length > 0)
    ? c.agents
    : (c.enableAgent && c.agentType
      ? [ { agentType: c.agentType, authType: c.authType, apiToken: c.apiToken } ]
      : []);

  // No agent enabled → IDE-only / bare import. Omit `agentConfigs` entirely;
  // emit only the root container toggles.
  if (entries.length === 0) {
    return root;
  }

  const agentConfigs: Partial<Record<ZagentAgentType, ZagentAgentConfig>> = {};
  for (const entry of entries) {
    agentConfigs[entry.agentType] = {
      authType: entry.authType,
      provider: AGENT_PROVIDER_MAP[entry.agentType],
      apiToken: entry.authType === 'token' ? entry.apiToken : undefined
    };
  }

  return { agentConfigs, ...root };
}

/** Legacy single-agent producer. Kept for tests / external callers. */
function toLegacyHandoffZagent(c: ZagentMiniConfig): RecipeHandoffZagentConfig {
  return {
    agentType: c.agentType,
    authType: c.authType,
    provider: c.provider,
    enableAgent: c.enableAgent,
    enableVscode: c.enableVscode,
    apiToken: c.apiToken,
    enablePublicAccess: c.enablePublicAccess,
    vscodePassword: c.vscodePassword,
    selectedServices: c.selectedServices
  };
}

/** @deprecated use `toHandoffPayloadSlice`. */
export const toHandoffZagent = toLegacyHandoffZagent;

/**
 * Recipe-handoff payload (either shape) → `ZagentMiniConfig` for the
 * downstream UI.
 *
 * `agentConfigs` is read in full: every entry becomes a `ZagentMiniAgentEntry`
 * in `agents[]` (order preserved). The legacy single-agent mirror fields
 * (`agentType`/`authType`/`provider`/`apiToken`) are populated from `agents[0]`
 * for the UI bits that are still single-agent; `enableAgent` is derived from
 * `agents.length > 0`. The Phase-2 auth-dialog queue walker iterates `agents`.
 *
 * IDE-only: when there is no `zagent`/`agentConfigs` but a root container
 * toggle is present, return a valid IDE-only config (`enableAgent:false`,
 * empty `agents`, `mode:'human'`) so the VS Code config is preserved.
 *
 * Returns `null` only when there is genuinely no agent AND no container
 * config at all (bare project/recipe import).
 */
export function toZagentMiniConfig(p: RecipeHandoffPayload): ZagentMiniConfig | null {
  if (p.zagent) return legacyZagentToMini(p.zagent);

  // Build the surviving agent list (entries may carry null/undefined values
  // from a partial wire payload, which the filter drops).
  const agents: ZagentMiniAgentEntry[] = p.agentConfigs
    ? (Object.entries(p.agentConfigs) as Array<[ ZagentAgentType, ZagentAgentConfig | undefined ]>)
      .filter((e): e is [ ZagentAgentType, ZagentAgentConfig ] => !!e[1])
      .map(([ agentType, cfg ]) => ({
        agentType,
        authType: cfg.authType,
        apiToken: cfg.apiToken
      }))
    : [];

  const primary = agents[0];

  // Only an agent config with at least one surviving entry is an ai-agent
  // deploy. If `agentConfigs` had keys but all values were empty, fall through
  // to the IDE-only / null handling below rather than returning a contradictory
  // `mode:'ai-agent'` with `enableAgent:false`.
  if (primary) {
    const enableVscode = p.enableVscode ?? false;
    return {
      mode: 'ai-agent',
      enableAgent: true,
      enableVscode,
      agentType: primary.agentType,
      authType: primary.authType,
      provider: AGENT_PROVIDER_MAP[primary.agentType],
      apiToken: primary.apiToken,
      agents,
      includeVscode: enableVscode,
      enablePublicAccess: p.enablePublicAccess ?? false,
      vscodePassword: p.vscodePassword,
      selectedServices: p.selectedServices ?? []
    };
  }

  // No agents, but a root container toggle is present → IDE-only deploy.
  // Returning a valid config (rather than null) preserves the VS Code setup.
  const hasContainerConfig =
    p.enableVscode === true
    || p.enablePublicAccess !== undefined
    || !!p.selectedServices?.length
    || p.vscodePassword !== undefined;

  if (hasContainerConfig) {
    const enableVscode = p.enableVscode ?? false;
    return {
      mode: 'human',
      enableAgent: false,
      enableVscode,
      agentType: 'claude-code',
      authType: 'oauth',
      provider: AGENT_PROVIDER_MAP['claude-code'],
      apiToken: undefined,
      agents: [],
      includeVscode: enableVscode,
      enablePublicAccess: p.enablePublicAccess ?? false,
      vscodePassword: p.vscodePassword,
      selectedServices: p.selectedServices ?? []
    };
  }

  return null;
}

function legacyZagentToMini(z: RecipeHandoffZagentConfig): ZagentMiniConfig {
  const enableAgent = z.enableAgent ?? true;
  const enableVscode = z.enableVscode ?? false;
  // Mirror the single legacy agent into `agents` so multi-agent consumers
  // (mini-card, auth-dialog walker) see it on the legacy read path too.
  const agents: ZagentMiniAgentEntry[] = enableAgent && z.agentType
    ? [ { agentType: z.agentType, authType: z.authType, apiToken: z.apiToken } ]
    : [];
  return {
    mode: enableAgent ? 'ai-agent' : 'human',
    enableAgent,
    enableVscode,
    agentType: z.agentType,
    authType: z.authType,
    provider: z.provider,
    apiToken: z.apiToken,
    agents,
    includeVscode: enableVscode,
    enablePublicAccess: z.enablePublicAccess ?? false,
    vscodePassword: z.vscodePassword,
    selectedServices: z.selectedServices ?? []
  };
}
